import React, { useEffect, useState, lazy, Suspense } from 'react';
import { SIGN_IN_REQUEST_EVENT } from '../../utils/wishlist';
import { useAuth } from '../../context/AuthContext';

const SignInSheet = lazy(() => import('./SignInSheet'));

/* ═══════════════════════════════════════════════════════════════════════════
   SignInGateway
   ─────────────
   Global listener mounted once at the app root. Any component anywhere in
   the tree can request the sign-in sheet by calling:

       window.dispatchEvent(new CustomEvent('hm-request-sign-in'))

   or by using the convenience helper from `utils/wishlist`:

       import { requestSignIn } from '../utils/wishlist';
       requestSignIn();

   The wishlist heart on hotel cards uses this to redirect guests through
   sign-in BEFORE writing anything to storage. Same mechanism can be reused
   for any auth-gated action (book now, contact host, etc.).

   - Re-entrant-safe: if the sheet is already open, additional events are
     no-ops (single-instance guarantee).
   - Already-signed-in guards: if the event fires while the user is already
     authenticated (race condition, late event), we ignore it.
   - SignInSheet itself is lazy-loaded so its bundle weight only lands when
     a guest actually attempts an auth-gated action — guest browsing of the
     public site stays as light as it was before.
   ═══════════════════════════════════════════════════════════════════════════ */
const SignInGateway = () => {
    const [open, setOpen] = useState(false);
    const auth = useAuth() || {};
    const signedIn = !!(auth.userSession || auth.user);

    useEffect(() => {
        const onRequest = () => {
            // Suppress if already signed in (handles races where the event
            // fires just as the auth state updates).
            if (auth.userSession || auth.user) return;
            setOpen(true);
        };
        window.addEventListener(SIGN_IN_REQUEST_EVENT, onRequest);
        return () => window.removeEventListener(SIGN_IN_REQUEST_EVENT, onRequest);
        // We intentionally do NOT depend on auth here — the listener reads
        // the live value of `auth` at fire time via closure, and we want a
        // single stable listener for the component lifetime.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Auto-close the sheet once the user successfully signs in.
    useEffect(() => {
        if (signedIn && open) setOpen(false);
    }, [signedIn, open]);

    if (!open) return null;
    return (
        <Suspense fallback={null}>
            <SignInSheet open={open} onClose={() => setOpen(false)} />
        </Suspense>
    );
};

export default SignInGateway;
